<script setup lang="ts">
/**
 * One exercise's form: the prompt (the shared text/image block list, addressed by the exercise's
 * id), the type, hint and explanation, then the type's own sub-form from
 * `MATH_EXERCISE_EDITORS`. Text anywhere may carry `$…$` KaTeX; the prompt's Preview tab renders it.
 */
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { AppSwitcherItem, AppSwitcherList } from '@shared/ui/switchers'
import { IconContainer } from '@shared/ui/other'
import { AppTextarea, NativeSelect } from '@shared/ui/fields'

import ExercisePromptPreview from './preview/ExercisePromptPreview.vue'
import ChapterContentList from '../ChapterContentList.vue'
import {
  KATEX_SYNTAX_URL,
  MATH_PROMPT_TABS,
  useMathExerciseEditor,
} from '../../../../model/assignments/math-exercise-editor.hook'

defineOptions({
  name: 'ExerciseEditor',
})

const {
  refExercise,
  refOwner,
  refLabel,
  refHint,
  refExplanation,
  refType,
  refTypeOptions,
  refTypeSelectKey,
  refTypeEditor,
  refPromptTab,
  setPromptTab,
  setHint,
  setExplanation,
  setType,
} = useMathExerciseEditor()
</script>

<template>
  <div class="exercise-editor">
    <AppText
      v-if="!refExercise || !refOwner"
      name="body2"
      color="slate-500"
    >
      Select an exercise to write it.
    </AppText>

    <template v-else>
      <AppText name="body1-medium">
        {{ refLabel }}
      </AppText>

      <div class="exercise-editor__row">
        <NativeSelect
          :key="refTypeSelectKey"
          name="exercise-type"
          label="Type"
          :value="refType"
          :placeholder-enabled="refType === null"
          placeholder="Unknown type — pick one"
          :options="refTypeOptions"
          class="exercise-editor__field"
          @change="(option: NativeSelectOption) => setType(String(option.value))"
        />
      </div>

      <div class="exercise-editor__prompt">
        <div class="exercise-editor__prompt-head">
          <div class="exercise-editor__prompt-title">
            <AppText name="body2-medium">
              Prompt
            </AppText>
            <AppTooltip
              text="KaTeX syntax: write math between $…$"
              placement="top"
            >
              <a
                :href="KATEX_SYNTAX_URL"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="KaTeX syntax reference"
                class="exercise-editor__syntax-link"
              >
                <IconContainer>
                  <FontAwesomeIcon :icon="faCircleInfo" />
                </IconContainer>
              </a>
            </AppTooltip>
          </div>
          <AppSwitcherList>
            <AppSwitcherItem
              v-for="tab in MATH_PROMPT_TABS"
              :key="tab.value"
              :text="tab.text"
              :selected="refPromptTab === tab.value"
              @click="setPromptTab(tab.value)"
            />
          </AppSwitcherList>
        </div>

        <!-- The reused block list: a prompt is authored exactly like a chapter's passage. -->
        <ChapterContentList
          v-if="refPromptTab === 'editor'"
          :owner="refOwner"
          hide-label
          editor-name="the math editor"
          text-placeholder="What the kid is asked — $…$ for math, e.g. $\frac{1}{2} + \frac{1}{4}$"
        />
        <ExercisePromptPreview
          v-else
          :exercise="refExercise"
        />
      </div>

      <component
        :is="refTypeEditor"
        v-if="refTypeEditor"
        :exercise="refExercise"
      />
      <AppText
        v-else
        name="body2"
        color="slate-500"
      >
        This exercise has a type the math editor does not know. Pick a type above to start it over.
      </AppText>

      <AppTextarea
        name="hint"
        label="Hint"
        optional-label="optional"
        :value="refHint"
        placeholder="A nudge the kid can ask for"
        @input="(event: AppTextareaEvent) => setHint(event.value)"
      />
      <AppTextarea
        name="explanation"
        label="Explanation"
        optional-label="optional"
        :value="refExplanation"
        placeholder="Shown after the kid answers"
        @input="(event: AppTextareaEvent) => setExplanation(event.value)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss" src="./ExerciseEditor.scss" />
